import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { resendLabelKey, useResendCooldown } from '../model/useResendCooldown';
import { EmailText } from './EmailText';

interface EmailConfirmNoteProps {
  email: string;
  /** Почта уже подтверждена (ответ на повторную отправку) — блок можно убрать. */
  onVerified?: () => void;
  /** Без «Подтверди почту — откроем свои слова», когда это уже говорит заголовок страницы. */
  showTitle?: boolean;
  className?: string;
}

/**
 * Короткий блок «подтверди почту» на итоге знакомства: свои слова открываются после
 * подтверждения, письмо ушло при регистрации, здесь — только повторная отправка
 * (та же логика и кулдаун, что у EmailVerifyBanner).
 */
export function EmailConfirmNote({ email, onVerified, showTitle = true, className }: EmailConfirmNoteProps) {
  const { t } = useTranslation();
  const { resend, state, cooldown, disabled } = useResendCooldown({ onAlreadyVerified: onVerified });

  return (
    <div className={cn('flex flex-col gap-3 rounded-[20px] border border-warn/30 bg-warn/10 p-4 sm:px-5', className)}>
      <div className={cn('flex min-w-0 gap-3', showTitle ? 'items-start' : 'items-center')}>
        <span
          aria-hidden
          className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-warn/20 text-base text-warn"
        >
          ✉
        </span>
        <div className="min-w-0">
          {showTitle && (
            <p className="mb-1 text-[15px] font-semibold leading-snug text-fg">{t('emailVerify.nudge.title')}</p>
          )}
          {/* Адрес — ПДн: amp-mask / ym-hide-content прячут его в записях сессий. */}
          <p className="text-[14px] leading-snug text-muted">
            {t('emailVerify.nudge.bodyPrefix')}{' '}
            <span className="amp-mask ym-hide-content text-fg [overflow-wrap:anywhere]">
              <EmailText email={email} />
            </span>
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={resend}
        disabled={disabled}
        className={cn(
          // На телефоне — во всю ширину под большой палец; шире — под текстом, с его отступом.
          'min-h-[44px] w-full whitespace-nowrap rounded-pill px-5 text-[14px] font-semibold tracking-[0.02em] transition-colors sm:ml-11 sm:w-auto sm:self-start',
          disabled ? 'cursor-not-allowed bg-elev text-muted' : 'bg-warn text-fg-inv hover:brightness-105',
        )}
      >
        {t(`emailVerify.full.${resendLabelKey[state]}`, { n: cooldown })}
      </button>
    </div>
  );
}
